import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import withStyles from "isomorphic-style-loader/withStyles";
import {
  Button,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import { ClipLoader, ScaleLoader } from "react-spinners";
import ModalFrame from "../../../../toolkit/modalFrame";
import IGlobalConfig from "../../../../types/IGlobalConfig";
import ICharity from "../../../../types/services/ICharity";
import IUser from "../../../../types/services/IUser";
import { buildAdminSessionFromLoginResponse } from "../../../../utils/adminAuthSession";
import { isProductionDeployment } from "../../../../utils/isProductionDeployment";
import {
  AvailabilityStatus,
  checkCharityId,
  createSuperadminCharity,
  fetchProvisionSuggestions,
  formatCharityProvisionError,
  getProvisionStatus,
  SuperadminCharityCreateResponse,
} from "../../../../utils/superadminCharityProvisionApi";
import {
  CHARITY_ID_MAX_LENGTH,
  DOMAIN_MAX_LENGTH,
  domainSuffixForEnvironment,
  sanitizeCharityIdInput,
  sanitizeDomainInput,
} from "../../../../utils/suggestSubdomainFromName";
import TooltipColorPicker from "../../charitySetup/onboarding/onboardingCustomation/components/leftSideBar/common/tooltipColorPicker";
import s from "./styles.scss";
import { ONBOARDING_DASHBOARD_PATH } from "../../../../utils/onboarding/stepNavigation";

const NAME_PATTERN = /^[A-Za-z0-9 \-]+$/;
const ID_PATTERN = /^[a-z0-9-]{1,40}$/;
const SUBDOMAIN_PATTERN = /^[a-z0-9-]+$/;
const DEFAULT_HIGHLIGHT_COLOR = "#0B4F8D";
const POLL_INTERVAL_MS = 2500;

const STEP_LABELS: Record<string, string> = {
  preflight: "Pre-flight checks",
  mysql: "Auth database",
  bitbucket: "Charity JSON branch",
  redis: "Redis configuration",
  rtdb: "Firebase data",
  extraMappings: "Alpha routing",
  cloudflare: "DNS (Cloudflare)",
  token: "Finalize",
};

const PROVISION_STEP_ORDER = [
  "preflight",
  "mysql",
  "bitbucket",
  "redis",
  "rtdb",
  "extraMappings",
  "cloudflare",
  "token",
];

function provisionStepLabel(step: number | undefined | null): string | null {
  if (step == null) {
    return null;
  }
  const key = PROVISION_STEP_ORDER[step];
  if (!key) {
    return null;
  }
  return STEP_LABELS[key] || key;
}

function currentProvisionStepLabel(currentStep: number | undefined | null): string {
  if (currentStep == null) {
    return STEP_LABELS.preflight;
  }
  const nextIndex = currentStep + 1;
  if (nextIndex >= PROVISION_STEP_ORDER.length) {
    return STEP_LABELS.token;
  }
  return provisionStepLabel(nextIndex) || STEP_LABELS.token;
}

function isValidCharityUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  const urlToTest = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(urlToTest);
    return url.hostname.includes(".");
  } catch {
    return false;
  }
}

function normalizeCharityUrl(value: string): string {
  const trimmed = value.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

type WizardPhase = "form" | "provisioning" | "error";

function isAvailabilityFailure(status: AvailabilityStatus | null): boolean {
  return (
    status === "live" || status === "provision_failed" || status === "invalid"
  );
}

function formatAvailabilityStatusMessage(
  status: AvailabilityStatus,
  detail?: string | null
): string {
  if (status === "live") {
    return "Status: Already in use";
  }
  if (status === "provision_failed") {
    if (detail) {
      return formatCharityProvisionError(detail, detail);
    }
    return "Previous charity setup failed with partial changes. Use a different ID or ask ops to clear the failed provision.";
  }
  return `Status: ${status}`;
}

function applyAvailabilityStatuses(
  result: {
    status?: AvailabilityStatus;
    idStatus?: AvailabilityStatus;
    subdomainStatus?: AvailabilityStatus;
    errorMessage?: string;
  },
  setIdStatus: (status: AvailabilityStatus | null) => void,
  setDomainStatus: (status: AvailabilityStatus | null) => void,
  setDetail?: (detail: string | null) => void
) {
  const fallback = result.status ?? null;
  setIdStatus(result.idStatus ?? fallback);
  setDomainStatus(result.subdomainStatus ?? fallback);
  if (setDetail) {
    const failed =
      result.status === "provision_failed" ||
      result.idStatus === "provision_failed" ||
      result.subdomainStatus === "provision_failed";
    setDetail(failed ? result.errorMessage ?? null : null);
  }
}

interface Props {
  isOpen: boolean;
  toggle: () => void;
  config?: IGlobalConfig;
  serviceAccessToken?: string;
  firebase: { login: (opts: { token: string }) => Promise<unknown> };
  loginUser: (user?: IUser, currentCharity?: ICharity, charities?: ICharity[]) => void;
  changeCurrentCharity: (charity: ICharity) => void;
  navigate: (path: string) => void;
}

function CreateCharityWizard(props: Props) {
  const {
    isOpen,
    toggle,
    config,
    serviceAccessToken,
    firebase,
    loginUser,
    changeCurrentCharity,
    navigate,
  } = props;

  const isProd = isProductionDeployment();
  const domainSuffix = domainSuffixForEnvironment(isProd);
  const [phase, setPhase] = useState<WizardPhase>("form");
  const [name, setName] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [charityId, setCharityId] = useState("");
  const [charityUrl, setCharityUrl] = useState("");
  const [isPrime, setIsPrime] = useState(false);
  const [highlightColor, setHighlightColor] = useState(DEFAULT_HIGHLIGHT_COLOR);
  const [idManuallyEdited, setIdManuallyEdited] = useState(false);
  const [subdomainManuallyEdited, setSubdomainManuallyEdited] = useState(false);
  const [suffixBanner, setSuffixBanner] = useState(false);
  const [idCheckStatus, setIdCheckStatus] = useState<AvailabilityStatus | null>(null);
  const [domainCheckStatus, setDomainCheckStatus] = useState<AvailabilityStatus | null>(null);
  const [availabilityDetail, setAvailabilityDetail] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [provisionError, setProvisionError] = useState<string | null>(null);
  const [provisionResponse, setProvisionResponse] =
    useState<SuperadminCharityCreateResponse | null>(null);
  const [provisionCompleteMessage, setProvisionCompleteMessage] = useState<string | null>(null);
  const [checkingId, setCheckingId] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [nameFieldBlurred, setNameFieldBlurred] = useState(false);
  const pollTimerRef = useRef<number | null>(null);
  const idManuallyEditedRef = useRef(idManuallyEdited);
  const subdomainManuallyEditedRef = useRef(subdomainManuallyEdited);

  useEffect(() => {
    idManuallyEditedRef.current = idManuallyEdited;
  }, [idManuallyEdited]);

  useEffect(() => {
    subdomainManuallyEditedRef.current = subdomainManuallyEdited;
  }, [subdomainManuallyEdited]);

  const resetWizard = useCallback(() => {
    setPhase("form");
    setName("");
    setSubdomain("");
    setCharityId("");
    setCharityUrl("");
    setIsPrime(false);
    setHighlightColor(DEFAULT_HIGHLIGHT_COLOR);
    setIdManuallyEdited(false);
    setSubdomainManuallyEdited(false);
    setSuffixBanner(false);
    setIdCheckStatus(null);
    setDomainCheckStatus(null);
    setAvailabilityDetail(null);
    setFormError(null);
    setProvisionError(null);
    setProvisionResponse(null);
    setProvisionCompleteMessage(null);
    setCheckingId(false);
    setSuggestionsLoading(false);
    setNameFieldBlurred(false);
    if (pollTimerRef.current != null) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetWizard();
    }
  }, [isOpen, resetWizard]);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current != null) {
        window.clearInterval(pollTimerRef.current);
      }
    };
  }, []);

  const adminUrl = config?.adminServiceUrl;
  const canCallApi = Boolean(adminUrl && serviceAccessToken);
  const formLocked = suggestionsLoading || checkingId;
  const secondaryFieldsDisabled = !nameFieldBlurred || formLocked;

  const fetchSuggestions = useCallback(
    async (nextName: string) => {
      if (!canCallApi || !nextName.trim()) {
        setIdCheckStatus(null);
        setDomainCheckStatus(null);
        setAvailabilityDetail(null);
        return;
      }
      setSuggestionsLoading(true);
      setFormError(null);
      try {
        const result = await fetchProvisionSuggestions(
          adminUrl as string,
          serviceAccessToken as string,
          nextName.trim()
        );
        if (!subdomainManuallyEditedRef.current) {
          setSubdomain(sanitizeDomainInput(result.suggestedSubdomain || ""));
        }
        if (!idManuallyEditedRef.current) {
          setCharityId(
            sanitizeCharityIdInput(result.charityId || result.suggestedId || "")
          );
        }
        setSuffixBanner(
          Boolean(result.suffixApplied && subdomainManuallyEditedRef.current)
        );
        applyAvailabilityStatuses(
          result,
          setIdCheckStatus,
          setDomainCheckStatus,
          setAvailabilityDetail
        );
      } catch {
        setIdCheckStatus("invalid");
        setDomainCheckStatus("invalid");
        setFormError("Could not load domain and ID suggestions.");
      } finally {
        setSuggestionsLoading(false);
      }
    },
    [adminUrl, canCallApi, serviceAccessToken]
  );

  const refreshIdAvailability = useCallback(
    async (nextId: string, nextSubdomain: string) => {
      if (!canCallApi || !nextId || !nextSubdomain) {
        setIdCheckStatus(null);
        setDomainCheckStatus(null);
        setAvailabilityDetail(null);
        return;
      }
      setCheckingId(true);
      try {
        const result = await checkCharityId(
          adminUrl as string,
          serviceAccessToken as string,
          nextId.toLowerCase(),
          nextSubdomain.toLowerCase()
        );
        applyAvailabilityStatuses(
          result,
          setIdCheckStatus,
          setDomainCheckStatus,
          setAvailabilityDetail
        );
      } catch {
        setIdCheckStatus("invalid");
        setDomainCheckStatus("invalid");
      } finally {
        setCheckingId(false);
      }
    },
    [adminUrl, canCallApi, serviceAccessToken]
  );

  const validateForm = (): string | null => {
    if (formLocked) {
      return "Waiting for availability check to finish.";
    }
    const trimmedName = name.trim();
    if (!NAME_PATTERN.test(trimmedName)) {
      return "Charity name may only contain letters, numbers, spaces, and hyphens.";
    }
    const normalizedSubdomain = subdomain.trim().toLowerCase();
    if (!SUBDOMAIN_PATTERN.test(normalizedSubdomain)) {
      return "Domain may only contain lowercase letters, numbers, and hyphens.";
    }
    const normalizedId = charityId.trim().toLowerCase();
    if (!ID_PATTERN.test(normalizedId)) {
      return "Charity ID must be 1–40 characters: lowercase letters, numbers, and hyphens.";
    }
    if (isAvailabilityFailure(idCheckStatus) || isAvailabilityFailure(domainCheckStatus)) {
      return "Charity ID or domain is not available.";
    }
    if (!highlightColor.trim()) {
      return "Highlight color is required.";
    }
    if (!isValidCharityUrl(charityUrl)) {
      return "Enter a valid charity URL (e.g. example.com or https://example.com).";
    }
    return null;
  };

  const handleClose = () => {
    resetWizard();
    toggle();
  };

  const goToConfirmOrSubmit = async () => {
    setFormError(null);
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }
    await submitProvision();
  };

  const applySessionAndNavigate = async (
    response: SuperadminCharityCreateResponse,
    charityName: string
  ) => {
    if (!response.session?.token || !response.session.user) {
      throw new Error("Provision completed without session token.");
    }
    await firebase.login({ token: response.session.token });
    const targetId = response.charityId;
    const { charities } = buildAdminSessionFromLoginResponse(
      {
        token: response.session.token,
        user: response.session.user,
        message: "",
      },
      loginUser,
      targetId
    );
    const match = charities.find(c => c.id === targetId);
    changeCurrentCharity({
      id: targetId,
      name: charityName,
      role: match?.role,
      disableGive: match?.disableGive,
    });
    navigate(ONBOARDING_DASHBOARD_PATH);
  };

  const handleTerminalResponse = async (
    response: SuperadminCharityCreateResponse
  ) => {
    setProvisionResponse(response);
    if (response.status === "completed" && response.session) {
      setProvisionCompleteMessage("Charity created successfully!");
      try {
        await new Promise<void>(resolve => {
          window.setTimeout(resolve, 1000);
        });
        await applySessionAndNavigate(response, name.trim());
        handleClose();
      } catch (err) {
        setProvisionCompleteMessage(null);
        setPhase("error");
        setProvisionError(
          formatCharityProvisionError(
            err,
            "Could not complete sign-in to the new charity."
          )
        );
      }
      return;
    }
    if (response.status === "failed") {
      setPhase("error");
      const stepLabel = provisionStepLabel(response.failedStep);
      const fallback = stepLabel
        ? `Setup failed during ${stepLabel}.`
        : "Charity setup failed.";
      setProvisionError(
        formatCharityProvisionError(response.errorMessage || fallback, fallback)
      );
      return;
    }
  };

  const startPolling = (targetCharityId: string) => {
    if (pollTimerRef.current != null) {
      window.clearInterval(pollTimerRef.current);
    }
    pollTimerRef.current = window.setInterval(async () => {
      if (!canCallApi) {
        return;
      }
      try {
        const status = await getProvisionStatus(
          adminUrl as string,
          serviceAccessToken as string,
          targetCharityId
        );
        setProvisionResponse(status);
        if (status.status === "in_progress") {
          return;
        }
        if (pollTimerRef.current != null) {
          window.clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
        await handleTerminalResponse(status);
      } catch (err) {
        if (pollTimerRef.current != null) {
          window.clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
        setPhase("error");
        setProvisionError(
          formatCharityProvisionError(
            err,
            "Could not check provisioning progress. Try closing and reopening this dialog."
          )
        );
      }
    }, POLL_INTERVAL_MS);
  };

  const submitProvision = async () => {
    if (!canCallApi) {
      setFormError("Missing admin service configuration.");
      return;
    }
    setFormError(null);
    setProvisionError(null);
    setPhase("provisioning");
    const payload = {
      name: name.trim(),
      subdomain: subdomain.trim().toLowerCase(),
      charityId: charityId.trim().toLowerCase(),
      isPrime,
      highlightColor: highlightColor.trim(),
      charityUrl: normalizeCharityUrl(charityUrl),
    };
    try {
      const response = await createSuperadminCharity(
        adminUrl as string,
        serviceAccessToken as string,
        payload
      );
      await handleTerminalResponse(response);
      if (response.status === "in_progress") {
        startPolling(payload.charityId);
      }
    } catch (err) {
      const isAbort =
        err instanceof Error &&
        (err.name === "AbortError" || err.message.includes("aborted"));
      if (isAbort) {
        startPolling(payload.charityId);
        return;
      }
      setPhase("error");
      setProvisionError(formatCharityProvisionError(err));
    }
  };

  const latestCompletedStepLabel = useMemo(() => {
    if (provisionResponse?.status !== "in_progress") {
      return null;
    }
    return provisionStepLabel(provisionResponse.currentStep);
  }, [provisionResponse]);

  const activeStepLabel = useMemo(() => {
    if (provisionResponse?.status !== "in_progress") {
      return null;
    }
    return currentProvisionStepLabel(provisionResponse.currentStep);
  }, [provisionResponse]);

  const renderAvailabilityHint = (
    status: AvailabilityStatus | null,
    loadingLabel: string,
    defaultHint: string
  ) => {
    if (suggestionsLoading) {
      return (
        <div className={s.hintRow}>
          <ClipLoader size={14} color="#1F65B1" />
          <span>Suggesting domain and ID…</span>
        </div>
      );
    }
    if (checkingId) {
      return (
        <div className={s.hintRow}>
          <ClipLoader size={14} color="#1F65B1" />
          <span>{loadingLabel}</span>
        </div>
      );
    }
    if (status === "available") {
      return <div className={s.hintSuccess}>Status: available</div>;
    }
    if (status) {
      const detail =
        status === "provision_failed" ? availabilityDetail : null;
      return (
        <div className={s.hintError}>
          {formatAvailabilityStatusMessage(status, detail)}
        </div>
      );
    }
    return <div className={s.hint}>{defaultHint}</div>;
  };

  const renderForm = () => (
    <>
      {formError && <div className={s.error}>{formError}</div>}
      {suffixBanner && (
        <div className={s.warning}>
          A numeric suffix was added to the charity ID because the base ID was
          taken. Domain was not changed — verify routing matches your intent.
        </div>
      )}
      <FormGroup className={s.field}>
        <Label className={s.label} for="createCharityName">
          Charity name
        </Label>
        <Input
          id="createCharityName"
          className={s.input}
          value={name}
          disabled={formLocked}
          onChange={e => {
            setName(e.target.value);
            setNameFieldBlurred(false);
            setIdManuallyEdited(false);
            setSubdomainManuallyEdited(false);
            idManuallyEditedRef.current = false;
            subdomainManuallyEditedRef.current = false;
            setIdCheckStatus(null);
            setDomainCheckStatus(null);
            setAvailabilityDetail(null);
          }}
          onBlur={() => {
            const trimmed = name.trim();
            if (trimmed) {
              setNameFieldBlurred(true);
              if (canCallApi) {
                fetchSuggestions(trimmed);
              }
            } else {
              setNameFieldBlurred(false);
            }
          }}
        />
      </FormGroup>
      <fieldset className={s.formFields} disabled={secondaryFieldsDisabled}>
        <FormGroup className={s.field}>
          <Label className={s.label} for="createCharityDomain">
            Domain
          </Label>
          <div className={s.domainInputGroup}>
            <Input
              id="createCharityDomain"
              className={s.domainInput}
              value={subdomain}
              maxLength={DOMAIN_MAX_LENGTH}
              onChange={e => {
                setSubdomainManuallyEdited(true);
                setSubdomain(sanitizeDomainInput(e.target.value));
                setIdCheckStatus(null);
                setDomainCheckStatus(null);
                setAvailabilityDetail(null);
              }}
              onBlur={() => refreshIdAvailability(charityId, subdomain)}
            />
            <span className={s.domainSuffix}>{domainSuffix}</span>
          </div>
          {renderAvailabilityHint(
            domainCheckStatus,
            "Checking domain availability…",
            "Required. Lowercase letters, numbers, and hyphens."
          )}
        </FormGroup>
        <FormGroup className={s.field}>
          <Label className={s.label} for="createCharityId">
            Charity ID
          </Label>
          <Input
            id="createCharityId"
            className={s.input}
            value={charityId}
            maxLength={CHARITY_ID_MAX_LENGTH}
            onChange={e => {
              setIdManuallyEdited(true);
              setCharityId(sanitizeCharityIdInput(e.target.value));
              setIdCheckStatus(null);
              setDomainCheckStatus(null);
              setAvailabilityDetail(null);
            }}
            onBlur={() => refreshIdAvailability(charityId, subdomain)}
          />
          {renderAvailabilityHint(
            idCheckStatus,
            "Checking charity ID availability…",
            "1–40 characters: lowercase letters, numbers, and hyphens."
          )}
        </FormGroup>
        <FormGroup className={s.field}>
          <Label className={s.label} for="createCharityUrl">
            Charity URL
          </Label>
          <Input
            id="createCharityUrl"
            className={s.input}
            value={charityUrl}
            onChange={e => setCharityUrl(e.target.value)}
          />
          <div className={s.hint}>
            Used for header redirect and logo links. You can enter example.com or a full https:// URL.
          </div>
        </FormGroup>
        <FormGroup className={s.field}>
          <label className={s.checkboxRow} htmlFor="createCharityPrime">
            <Input
              type="checkbox"
              id="createCharityPrime"
              checked={isPrime}
              onChange={e => setIsPrime(e.target.checked)}
            />
            <span className={s.checkboxText}>Missionary</span>
          </label>
        </FormGroup>
        <FormGroup className={s.field}>
          <Label className={s.label} for="createCharityColor">
            Highlight color
          </Label>
          <div className={s.colorPickerWrap}>
            <TooltipColorPicker
              value={highlightColor}
              setValue={setHighlightColor}
              maxInputWidth="140px"
            />
          </div>
        </FormGroup>
      </fieldset>
      <div className={s.actions}>
        <button type="button" className={s.cancelText} onClick={handleClose}>
          Cancel
        </button>
        <Button
          className={s.primaryButton}
          disabled={!nameFieldBlurred || formLocked}
          onClick={goToConfirmOrSubmit}
        >
          Create charity
        </Button>
      </div>
    </>
  );

  const renderProvisioning = () => {
    if (provisionCompleteMessage) {
      return (
        <div className={s.provisioningPanel}>
          <div className={s.success}>{provisionCompleteMessage}</div>
        </div>
      );
    }

    return (
      <>
        <p>Creating charity infrastructure. This may take a few minutes.</p>
        <div className={s.provisioningPanel}>
          <div className={s.spinnerWrap}>
            <ScaleLoader color="#0B4F8D" />
          </div>
          <div className={s.statusLine}>
            <span className={s.statusLabel}>Status:</span>{" "}
            {activeStepLabel || "Starting…"}
          </div>
          {latestCompletedStepLabel && (
            <div className={s.latestStep}>{latestCompletedStepLabel}</div>
          )}
        </div>
      </>
    );
  };

  const renderError = () => {
    const failedStepName = provisionStepLabel(provisionResponse?.failedStep);
    return (
    <>
      {provisionError && <div className={s.error}>{provisionError}</div>}
      {failedStepName && (
        <div className={s.hint}>Failed during: {failedStepName}</div>
      )}
      {provisionResponse?.artifacts && provisionResponse.artifacts.length > 0 && (
        <div className={s.hint}>
          Partial setup completed: {provisionResponse.artifacts.join(", ")}
        </div>
      )}
      <div className={s.actions}>
        <Button color="secondary" onClick={handleClose}>
          Close
        </Button>
      </div>
    </>
    );
  };

  const title =
    phase === "error"
      ? "Create failed"
      : phase === "provisioning"
      ? "Creating charity"
      : "Create a new Charity";

  return (
    <ModalFrame
      isOpen={isOpen}
      toggle={handleClose}
      title={title}
      size="md"
      centered
      contentClassName={s.wizardModalContent}
    >
      {phase === "form" && renderForm()}
      {phase === "provisioning" && renderProvisioning()}
      {phase === "error" && renderError()}
    </ModalFrame>
  );
}

export default withStyles(s)(CreateCharityWizard);
